import React, { useState, useMemo } from 'react';
import { Search, Filter, ArrowUpDown, X, Wrench } from 'lucide-react';
import type { Machine, MachineStatus } from '../../types';
import { evaluateMachine, STATUS_SEVERITY } from '../../services/scheduler';
import { MachineCard } from './MachineCard';

export type StatusFilterOption = 'ALL' | 'NORMAL' | 'DUE_SOON' | 'OVERDUE';
export type SortOption = 'SEVERITY' | 'ID' | 'NAME' | 'HOURS_DESC' | 'HOURS_ASC' | 'LAST_INSPECTED';

export interface MachineListProps {
  machines: Machine[];
  selectedMachineId?: string;
  onSelectMachine?: (machineId: string) => void;
  onStartInspection?: (machineId: string) => void;
  onAddHours?: (machineId: string, currentHours: number) => void;
  className?: string;
}

export function MachineList({
  machines,
  selectedMachineId,
  onSelectMachine,
  onStartInspection,
  onAddHours,
  className = '',
}: MachineListProps) {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption>('ALL');
  const [sortOption, setSortOption] = useState<SortOption>('SEVERITY');

  // Compute live machine evaluations for accurate filtering/sorting
  const machineEvaluations = useMemo(() => {
    const map = new Map<string, ReturnType<typeof evaluateMachine>>();
    for (const m of machines) {
      map.set(m.id, evaluateMachine(m));
    }
    return map;
  }, [machines]);

  // Filter and sort machines
  const filteredMachines = useMemo(() => {
    let list = [...machines];

    // 1. Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (m) =>
          m.id.toLowerCase().includes(q) ||
          m.name.toLowerCase().includes(q) ||
          m.model.toLowerCase().includes(q) ||
          (m.serialNo && m.serialNo.toLowerCase().includes(q))
      );
    }

    // 2. Status filter
    if (statusFilter !== 'ALL') {
      list = list.filter((m) => {
        const evaluated = machineEvaluations.get(m.id);
        const st = evaluated?.overallStatus || m.status;
        return st === statusFilter;
      });
    }

    // 3. Sorting
    list.sort((a, b) => {
      const evalA = machineEvaluations.get(a.id);
      const evalB = machineEvaluations.get(b.id);
      const statusA = evalA?.overallStatus || a.status;
      const statusB = evalB?.overallStatus || b.status;

      switch (sortOption) {
        case 'SEVERITY': {
          const sevA = STATUS_SEVERITY[statusA as MachineStatus] || 1;
          const sevB = STATUS_SEVERITY[statusB as MachineStatus] || 1;
          if (sevA !== sevB) return sevB - sevA; // High severity first
          return (b.cumulativeHours || 0) - (a.cumulativeHours || 0);
        }
        case 'ID':
          return a.id.localeCompare(b.id);
        case 'NAME':
          return a.name.localeCompare(b.name);
        case 'HOURS_DESC':
          return (b.cumulativeHours || 0) - (a.cumulativeHours || 0);
        case 'HOURS_ASC':
          return (a.cumulativeHours || 0) - (b.cumulativeHours || 0);
        case 'LAST_INSPECTED': {
          const timeA = a.lastInspectionDate ? new Date(a.lastInspectionDate).getTime() : 0;
          const timeB = b.lastInspectionDate ? new Date(b.lastInspectionDate).getTime() : 0;
          return timeB - timeA;
        }
        default:
          return 0;
      }
    });

    return list;
  }, [machines, searchQuery, statusFilter, sortOption, machineEvaluations]);

  // Status counts for badge tabs
  const counts = useMemo(() => {
    let normal = 0;
    let due = 0;
    let overdue = 0;

    for (const m of machines) {
      const evalRes = machineEvaluations.get(m.id);
      const st = evalRes?.overallStatus || m.status;
      if (st === 'OVERDUE') overdue++;
      else if (st === 'DUE_SOON') due++;
      else normal++;
    }

    return { all: machines.length, normal, due, overdue };
  }, [machines, machineEvaluations]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Controls Bar: Search & Status Filter & Sort */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Search Bar */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="ค้นหาชื่อเครื่องจักร, ID, Model, Serial..."
            className="w-full bg-white border border-slate-300 rounded-xl pl-10 pr-10 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[48px] shadow-xs"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Sort Selector */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:w-56">
            <ArrowUpDown className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              className="w-full bg-white border border-slate-300 rounded-xl pl-9 pr-8 py-2.5 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[48px] appearance-none cursor-pointer shadow-xs"
            >
              <option value="SEVERITY">เรียงตามความเร่งด่วน (Severity)</option>
              <option value="HOURS_DESC">ชั่วโมงทำงานมากสุด (Hours High-Low)</option>
              <option value="HOURS_ASC">ชั่วโมงทำงานน้อยสุด (Hours Low-High)</option>
              <option value="NAME">ชื่อเครื่องจักร (A-Z)</option>
              <option value="ID">รหัสเครื่องจักร (Machine ID)</option>
              <option value="LAST_INSPECTED">ตรวจเช็กล่าสุด (Recently Checked)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Status Filter Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
        <button
          type="button"
          onClick={() => setStatusFilter('ALL')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors min-h-[40px] flex items-center gap-1.5 ${
            statusFilter === 'ALL'
              ? 'bg-blue-600 text-white shadow-xs'
              : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-2xs'
          }`}
        >
          ทั้งหมด ({counts.all})
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('OVERDUE')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors min-h-[40px] flex items-center gap-1.5 ${
            statusFilter === 'OVERDUE'
              ? 'bg-rose-600 text-white shadow-xs'
              : 'bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 shadow-2xs'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-rose-500" />
          เกินกำหนด ({counts.overdue})
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('DUE_SOON')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors min-h-[40px] flex items-center gap-1.5 ${
            statusFilter === 'DUE_SOON'
              ? 'bg-amber-600 text-white shadow-xs'
              : 'bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 shadow-2xs'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          ใกล้ครบกำหนด ({counts.due})
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('NORMAL')}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors min-h-[40px] flex items-center gap-1.5 ${
            statusFilter === 'NORMAL'
              ? 'bg-emerald-600 text-white shadow-xs'
              : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 shadow-2xs'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          ปกติ ({counts.normal})
        </button>
      </div>

      {/* Machine Cards Grid */}
      {filteredMachines.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredMachines.map((machine) => (
            <MachineCard
              key={machine.id}
              machine={machine}
              isSelected={machine.id === selectedMachineId}
              onSelect={onSelectMachine}
              onStartInspection={onStartInspection}
              onAddHours={onAddHours}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-3 shadow-xs">
          <div className="p-3 bg-slate-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto text-slate-500">
            <Wrench className="w-6 h-6" />
          </div>
          <p className="text-base font-semibold text-slate-800">
            ไม่พบเครื่องจักรที่ตรงกับเงื่อนไขการค้นหา
          </p>
          <p className="text-xs text-slate-500">
            ลองปรับเปลี่ยนคำค้นหา หรือรีเซ็ตตัวกรองสถานะ
          </p>
          {(searchQuery || statusFilter !== 'ALL') && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('ALL');
              }}
              className="text-xs text-blue-600 hover:text-blue-700 font-semibold underline pt-1"
            >
              ล้างตัวกรองทั้งหมด
            </button>
          )}
        </div>
      )}
    </div>
  );
}
